import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Award, Users, Target, Globe } from "lucide-react";
import { IMAGES, TEAM } from "../lib/constants";

const PILLARS = [
  {
    icon: Target,
    title: "Specialization",
    body: "Mature and tail-end brand revitalization is the only thing we do — and we do it exceptionally well.",
  },
  {
    icon: Users,
    title: "Delta Field Force",
    body: "A generation of young, locally-rooted pharmacy graduates reshaping pharma's extra-urban reach.",
  },
  {
    icon: Globe,
    title: "Extra-Urban India",
    body: "300+ market HQs with deep penetration into the Tier 2–4 markets others overlook.",
  },
  {
    icon: Award,
    title: "Mantrepreneurship",
    body: "A culture of entrepreneurial thinking and ownership — on our team, and in partnership with our clients.",
  },
];

const MILESTONES = [
  { year: "2019", title: "Medstry founded", body: "Launched with a bold thesis: India's mature and tail-end pharma brands are a growth engine waiting to be unlocked." },
  { year: "2021", title: "3D Phygital model", body: "HCP + Digital + Channel engine operationalized across multiple therapy areas." },
  { year: "2023", title: "Scaled Channel Team", body: "60,000+ chemist network, 55,000+ HCP touchpoints — pan-India coverage established." },
  { year: "2025", title: "Brand Lifecycle Partner of Choice", body: "Working with Top-10 Indian pharma and select MNCs entering India's extra-urban markets." },
];

export default function About() {
  return (
    <main className="pt-32 md:pt-40 pb-24" data-testid="about-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 mb-10">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-20 md:mb-28">
          <div className="lg:col-span-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">About Medstry</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[0.98] text-slate-900">
              Specialized in <br />
              <span className="font-editorial italic font-medium text-slate-700">what others overlook.</span>
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Medstry is a specialized healthcare sales and marketing services company focused on mature
              and tail-end brand revitalization through high-ROI solutions — working where most field
              forces don't.
            </p>
          </div>
        </div>

        {/* Pillars */}
        <section className="mb-24 md:mb-32" data-testid="about-pillars">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-white p-8"
                >
                  <Icon className="h-7 w-7 text-teal-600 mb-6" strokeWidth={1.5} />
                  <h3 className="font-display text-xl font-bold text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Founder Quote */}
        <section className="mb-24 md:mb-32 bg-[#0B0F19] text-white p-10 md:p-20 relative overflow-hidden" data-testid="about-founder-quote">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-300 mb-6">
              From the Founder
            </div>
            <blockquote className="font-editorial italic text-3xl md:text-4xl lg:text-5xl leading-[1.15] mb-8">
              "Every mature and tail-end brand deserves dedicated attention and strategic investment.
              We make it economically viable through innovative talent deployment and
              performance-based economics."
            </blockquote>
            <div className="text-sm">
              <div className="font-bold">Inderjit Sood</div>
              <div className="text-slate-400">Co-Founder & Managing Director</div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="mb-24 md:mb-32" data-testid="about-team">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Leadership</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 leading-[1.04]">
              Founder-led, <br />
              <span className="font-editorial italic font-medium text-slate-700">operator-built.</span>
            </h2>
          </div>

          <div className="space-y-px bg-slate-200">
            {TEAM.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`team-member-${i}`}
                className="bg-white p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8"
              >
                <div className="lg:col-span-4">
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-700 mb-3">
                    {p.role}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                    {p.name}
                  </h3>
                  <div className="text-sm text-slate-500">{p.tenure}</div>
                </div>
                <div className="lg:col-span-8">
                  <p className="text-slate-700 text-[15px] leading-relaxed mb-5">{p.bio}</p>
                  {p.focus?.length > 0 && (
                    <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-5">
                      {p.focus.map((c) => (
                        <span
                          key={c}
                          className="inline-flex items-center px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 bg-slate-100 border border-slate-200"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Broader team — generic */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="team-broader"
              className="bg-white p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8"
            >
              <div className="lg:col-span-4">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-700 mb-3">
                  The Wider Team
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                  Operators, analysts, and field leaders.
                </h3>
                <div className="text-sm text-slate-500">Pan-India coverage</div>
              </div>
              <div className="lg:col-span-8">
                <p className="text-slate-700 text-[15px] leading-relaxed mb-5">
                  Behind the founder sits a focused team of industry veterans, analytics and
                  operations specialists, and a Delta Field Force of young pharmacy graduates
                  across Tier 2–4 India. Together, they translate strategy into measurable on-ground
                  outcomes — covering 300+ market HQs, 55,000+ HCP touchpoints, and 60,000+ chemists.
                </p>
                <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-5">
                  {["Industry Veterans", "Analytics & Operations", "Delta Field Force", "Channel Execution"].map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 bg-slate-100 border border-slate-200"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Timeline */}
        <section className="mb-24" data-testid="about-timeline">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Our Journey</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 leading-[1.04]">
              From bold thesis <br />
              <span className="font-editorial italic font-medium text-slate-700">to industry category.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-slate-200">
            {MILESTONES.map((m, i) => (
              <motion.div
                key={m.year}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white p-8"
              >
                <div className="font-display text-3xl md:text-4xl font-extrabold tnum text-teal-700 mb-5">
                  {m.year}
                </div>
                <h4 className="font-display text-lg font-bold text-slate-900 mb-2">{m.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{m.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-slate-50 border border-slate-200 p-10 md:p-16 text-center">
          <h3 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 max-w-2xl mx-auto">
            Let's talk about your portfolio.
          </h3>
          <p className="text-slate-600 max-w-2xl mx-auto mb-6">
            A 30-minute conversation can reveal the revenue hiding in your mature & tail-end brand catalog.
          </p>
          <Link
            to="/#contact"
            className="inline-flex items-center gap-2 btn-primary px-6 py-3.5 text-sm font-semibold"
          >
            Request Portfolio Analysis <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </main>
  );
}
